/* ESSENTIA 2.0 — upgrade seguro de 5 a 10 ml en carrito.
 * Refresca el carrito mediante los eventos nativos de Horizon/Shopify, SIN recargar la página.
 * Mantiene abierto el drawer y conserva las verificaciones del Pack Árabe.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

(() => {
  let inFlight = false;
  const select = '[data-essentia-upgrade-button]';

  function normalize(value) {
    return String(value || '').toLowerCase().replace(/[\s.]+/g, '').trim();
  }

  function hasSize(value, size) {
    const normalized = normalize(value);
    const prefix = String(size) + 'ml';
    return normalized === prefix ||
      normalized.startsWith(prefix + '-') ||
      normalized.startsWith(prefix + '–') ||
      normalized.startsWith(prefix + '—');
  }

  function validatesInventory(variant, quantity) {
    const stock = Number(variant?.inventory_quantity);
    const isInventoryTracked = Boolean(variant?.inventory_management);
    const forbidsOverselling = variant?.inventory_policy === 'deny';
    return !isInventoryTracked || !forbidsOverselling || !Number.isFinite(stock) || stock >= quantity;
  }

  function rootPath() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : root + '/';
  }

  async function requestJSON(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.status >= 400 || payload.errors) {
      throw new Error(payload.description || payload.message || 'No se pudo actualizar el carrito.');
    }
    return payload;
  }

  function isStandardLine(item) {
    if (!item || item.selling_plan_allocation) return false;
    const properties = item.properties;
    return !properties || (typeof properties === 'object' && Object.keys(properties).length === 0);
  }

  function notice(button, message) {
    const feedback = button.closest('[data-essentia-upgrade]')?.querySelector('[data-essentia-upgrade-feedback]');
    if (feedback) {
      feedback.textContent = message;
      feedback.hidden = !message;
    }
  }

  document.addEventListener('click', async (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest(select);
    if (!(button instanceof HTMLButtonElement)) return;
    event.preventDefault();
    if (inFlight || button.disabled) return;

    const sourceKey = button.dataset.sourceKey;
    const sourceVariantId = button.dataset.sourceVariant;
    const targetVariantId = button.dataset.targetVariant;
    const productId = button.dataset.productId;
    const handle = button.dataset.productHandle;
    const intendedQuantity = Number(button.dataset.quantity);

    if (!sourceKey || !/^\d+$/.test(String(sourceVariantId)) || !/^\d+$/.test(String(targetVariantId)) ||
        !/^\d+$/.test(String(productId)) || !/^[a-z0-9-]+$/.test(String(handle)) ||
        !Number.isSafeInteger(intendedQuantity) || intendedQuantity < 1) {
      notice(button, 'No se pudo identificar la variante. Actualizá el carrito.');
      return;
    }

    const originalLabel = button.innerHTML;
    inFlight = true;
    button.disabled = true;
    notice(button, 'Comprobando disponibilidad...');
    try {
      const root = rootPath();
      const [cart, product] = await Promise.all([
        requestJSON(root + 'cart.js'),
        requestJSON(root + 'products/' + handle + '.js'),
      ]);
      if (!Array.isArray(cart.items) || !Array.isArray(product.variants)) {
        throw new Error('No pudimos verificar los productos. Intentá nuevamente.');
      }

      const sourceRows = cart.items.filter(row => String(row.variant_id) === String(sourceVariantId));
      if (sourceRows.length !== 1) {
        throw new Error('El carrito cambió. Actualizá la página antes de continuar.');
      }
      const current = sourceRows[0];
      if (String(current.key) !== sourceKey ||
          String(current.product_id) !== String(productId) ||
          Number(current.quantity) !== intendedQuantity ||
          !hasSize(current.variant_title, 5) ||
          !isStandardLine(current)) {
        throw new Error('El carrito cambió. Actualizá la página antes de continuar.');
      }
      const target = product.variants.find(v => String(v.id) === String(targetVariantId));
      const source = product.variants.find(v => String(v.id) === String(sourceVariantId));
      if (!source || !target || !target.available || !hasSize(source.title, 5) ||
          !hasSize(target.title, 10) ||
          Number(target.price) <= Number(source.price)) {
        throw new Error('La presentación de 10 ml ya no está disponible.');
      }

      const existingTargetRows = cart.items.filter(row => String(row.variant_id) === String(targetVariantId));
      if (existingTargetRows.length > 1 || existingTargetRows.some(row => !isStandardLine(row))) {
        throw new Error('Hay otra presentación personalizada en el carrito. Cambiá el tamaño desde la ficha del producto.');
      }
      const existingTarget = existingTargetRows[0];
      const totalTargetQuantity = intendedQuantity + Number(existingTarget?.quantity || 0);
      if (!Number.isSafeInteger(totalTargetQuantity) || totalTargetQuantity < 1) {
        throw new Error('Cantidad no válida.');
      }

      if (!validatesInventory(target, totalTargetQuantity)) {
        throw new Error('No hay suficientes unidades de 10 ml disponibles.');
      }

      const updates = {};
      updates[sourceKey] = 0;
      updates[existingTarget?.key || String(targetVariantId)] = totalTargetQuantity;

      // Pedir HTML actualizado de las secciones que ya usan los componentes
      // nativos de Horizon; el panel <theme-drawer> queda montado y abierto.
      const sections = Array.from(new Set([
        'cart-drawer-section',
        ...Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
          .map((component) => component.dataset.sectionId)
          .filter(Boolean),
      ])).slice(0, 5);

      notice(button, 'Cambiando a 10 ml...');
      const result = await requestJSON(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const remaining5 = (result.items || []).filter(row => String(row.variant_id) === String(sourceVariantId));
      const total10 = (result.items || [])
        .filter(row => String(row.variant_id) === String(targetVariantId))
        .reduce((sum, row) => sum + Number(row.quantity || 0), 0);
      if (!Array.isArray(result.items) || remaining5.length || total10 < totalTargetQuantity) {
        throw new Error('La actualización necesita verificarse. Recargá el carrito antes de seguir.');
      }
      notice(button, 'Listo, actualizando tu pedido...');

      // Misma notificación empleada en el cart nativo para sincronizar
      // líneas, cantidades, total, progreso de envío, recomendaciones y contador.
      // action='update' evita que el panel sea abierto o cerrado automáticamente.
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [
          { id: sourceKey, quantity: 0 },
          { id: existingTarget?.key || String(targetVariantId), quantity: totalTargetQuantity },
        ],
        promise: deferred.promise,
      }));
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
        detail: {
          sections: result.sections || {},
          items: result.items,
          itemCount: result.item_count,
          source: 'essentia-cart-size-upgrade',
          didError: false,
        },
      });
    } catch (error) {
      notice(button, error?.message || 'No se pudo cambiar el tamaño. Intentá nuevamente.');
      if (button.isConnected) {
        button.disabled = false;
        button.innerHTML = originalLabel;
      }
    } finally {
      inFlight = false;
    }
  });
})();
